import { AcceptInviteShell } from "@/components/mentor-profile/accept-invite/accept-invite-shell";
import { OtpStep } from "@/components/mentor-profile/accept-invite/otp-step";
import { PasswordStep } from "@/components/mentor-profile/accept-invite/password-step";
import { WelcomeStep } from "@/components/mentor-profile/accept-invite/welcome-step";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import {
  useActivateMentorAccount,
  useResolveMentorInvite,
  useSendMentorInviteOtp,
} from "@/lib/api/mentor-profile.ts";
import { hashPassword } from "@/lib/utils";
import { useIntercom } from "@/lib/hooks/use-intercom";
import { PostHogEvents, useTrackEvent, useTrackPage } from "@/lib/posthog-events";
import { getStytchErrorMessage } from "@/lib/utils/stytch-errors";
import { isFullyAuthedSession } from "@/lib/auth/is-fully-authed-session";
import { useStytch, useStytchSession } from "@stytch/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";

interface AcceptInviteSearch {
  token: string;
}

export const Route = createFileRoute("/_auth/accept-invite")({
  validateSearch: (search: Record<string, unknown>): AcceptInviteSearch => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  component: AcceptInvite,
});

type Step = "welcome" | "otp" | "password";

function AcceptInvite() {
  const { token } = Route.useSearch();
  const stytchClient = useStytch();
  const { session } = useStytchSession();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { track } = useTrackEvent();
  useIntercom(undefined, "onboarding");

  const { data: invite, isLoading, isError } = useResolveMentorInvite(token);
  const sendOtpMutation = useSendMentorInviteOtp();
  const activateMutation = useActivateMentorAccount();

  const [step, setStep] = useState<Step>("welcome");
  const [methodId, setMethodId] = useState<string | null>(null);
  const [activatedEmail, setActivatedEmail] = useState<string | null>(null);

  useTrackPage(
    PostHogEvents.MENTOR_PROFILE_ONBOARDING_EMAIL_CLICKED,
    undefined,
    { enabled: Boolean(token) },
  );

  const isFullyAuthed = isFullyAuthedSession(session);

  useEffect(() => {
    if (!invite?.alreadyActivated || !isFullyAuthed) return;
    void router.navigate({ to: "/mentor-profile", replace: true });
  }, [invite?.alreadyActivated, isFullyAuthed, router]);

  const handleGetStarted = async () => {
    const { methodId: id } = await sendOtpMutation.mutateAsync({ token });
    setMethodId(id);
    track(PostHogEvents.MENTOR_PROFILE_ONBOARDING_STARTED);
    setStep("otp");
  };

  const handleVerifyOtp = async (otp: string) => {
    if (!methodId) throw new Error("Your code expired. Please resend it.");
    try {
      const response = await stytchClient.otps.authenticate(otp, methodId, {
        session_duration_minutes: 60 * 24,
      });
      if (!response.session?.session_id) {
        throw new Error("That code didn't work.");
      }
      setStep("password");
    } catch (e) {
      throw new Error(getStytchErrorMessage(e));
    }
  };

  const handleResendOtp = async () => {
    const { methodId: id } = await sendOtpMutation.mutateAsync({ token });
    setMethodId(id);
  };

  const handleCreatePassword = async (password: string) => {
    if (!activatedEmail) {
      const { hash, value: hashedPassword } = await hashPassword(password);
      const { email } = await activateMutation.mutateAsync({
        hash,
        hashedPassword,
      });
      setActivatedEmail(email);
    }
    try {
      await stytchClient.passwords.resetBySession({
        password,
        session_duration_minutes: 60 * 24,
      });
    } catch (e) {
      throw new Error(getStytchErrorMessage(e));
    }
    track(PostHogEvents.MENTOR_PROFILE_PASSWORD_CREATED);
    await queryClient.invalidateQueries({ queryKey: ["current-user"] });
    await router.navigate({ to: "/mentor-profile", replace: true });
  };

  if (isLoading || (invite?.alreadyActivated && isFullyAuthed)) {
    return (
      <div className="flex min-h-dvh w-full items-center justify-center bg-gradient-to-b from-[#ffede6] to-[#faf6ef]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !invite) {
    return (
      <AcceptInviteShell title="This invite link isn't working">
        <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
          It may have expired. Reach out to whoever invited you and they can send
          a fresh one.
        </p>
      </AcceptInviteShell>
    );
  }

  if (invite.alreadyActivated) {
    return (
      <AcceptInviteShell
        title="You're already set up"
        subtitle="Sign in with your email and password to pick up where you left off."
      >
        <Button
          asChild
          className="mt-8 h-[52px] w-full rounded-full bg-black text-base font-semibold text-white hover:bg-black/90"
        >
          <Link to="/login">Log in</Link>
        </Button>
      </AcceptInviteShell>
    );
  }

  switch (step) {
    case "welcome":
      return (
        <WelcomeStep
          firstName={invite.firstName}
          onGetStarted={handleGetStarted}
          isSending={sendOtpMutation.isPending}
        />
      );
    case "otp":
      return (
        <OtpStep
          maskedPhone={invite.maskedPhone}
          onVerify={handleVerifyOtp}
          onResend={handleResendOtp}
        />
      );
    case "password":
      return <PasswordStep onSubmit={handleCreatePassword} />;
    default: {
      const exhaustive: never = step;
      return exhaustive;
    }
  }
}
